import type { 
  JiraIssue, 
  JiraPersonSummary, 
  JiraPersonDetail, 
  JiraPeopleOverviewData,
} from '@/types/jira'

// ============================================================
// JIRA PERFORMANCE METRICS UTILITY FUNCTIONS
// Projenin amacı: Ekip iletişim performansını ölçmek ve iyileştirmek
// ============================================================

export interface PerformanceMetrics {
  completionRate: number       // Görev tamamlama oranı (%)
  avgResponseTime: number      // Ortalama yanıt süresi (saat)
  workloadScore: number        // İş yükü dengesi skoru (0-100)
  velocityScore: number        // Üretkenlik skoru (haftalık)
  blockedRatio: number         // Engellenmiş görev oranı (%)
  overallScore: number         // Genel performans skoru (0-100)
}

export interface TeamMetrics {
  totalMembers: number
  totalOpenTasks: number
  totalCompletedTasks: number
  avgCompletionRate: number
  teamVelocity: number         // Haftalık tamamlanan görev sayısı
  workloadBalance: number      // 0-100 (100 = mükemmel dağılım)
}

export interface DailyMetric {
  date: string
  value: number
  count?: number
}

export interface StatusDistribution {
  name: string
  value: number
  color: string
  percentage: number
}

export interface ProjectDistribution {
  name: string
  key: string
  value: number
  percentage: number
}

// ============================================================
// PERFORMANS HESAPLAMA FONKSİYONLARI
// ============================================================

/**
 * Görev tamamlama oranı hesapla
 */
export function calculateCompletionRate(openTasks: number, completedTasks: number): number {
  const total = openTasks + completedTasks
  if (total === 0) return 0
  return Math.round((completedTasks / total) * 100)
}

/**
 * Ortalama yanıt süresini hesapla (saat cinsinden)
 * created -> updated arasındaki ortalama süre
 */
export function calculateAvgResponseTime(issues: JiraIssue[]): number {
  if (issues.length === 0) return 0
  
  const responseTimes = issues.map(issue => {
    const created = new Date(issue.fields.created).getTime()
    const updated = new Date(issue.fields.updated).getTime()
    return (updated - created) / (1000 * 60 * 60) // Saat
  })
  
  const avg = responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length
  return Math.round(avg * 10) / 10 // 1 ondalık
}

/**
 * Görev çözüm süresini hesapla (gün cinsinden)
 */
export function calculateResolutionTime(issues: JiraIssue[]): number {
  const resolvedIssues = issues.filter(i => i.fields.resolutiondate)
  if (resolvedIssues.length === 0) return 0
  
  const resolutionTimes = resolvedIssues.map(issue => {
    const created = new Date(issue.fields.created).getTime()
    const resolved = new Date(issue.fields.resolutiondate!).getTime()
    return (resolved - created) / (1000 * 60 * 60 * 24) // Gün
  })
  
  const avg = resolutionTimes.reduce((sum, time) => sum + time, 0) / resolutionTimes.length
  return Math.round(avg * 10) / 10
}

/**
 * Haftalık velocity hesapla (son 30 günde tamamlanan / 4 hafta)
 */
export function calculateVelocity(completedTasksLast30Days: number): number {
  return Math.round(completedTasksLast30Days / 4)
}

/**
 * İş yükü dengesi skoru hesapla
 * Düşük standart sapma = yüksek denge = yüksek skor
 */
export function calculateWorkloadBalance(taskCounts: number[]): number {
  if (taskCounts.length === 0) return 100
  
  const mean = taskCounts.reduce((sum, c) => sum + c, 0) / taskCounts.length
  const variance = taskCounts.reduce((sum, c) => sum + Math.pow(c - mean, 2), 0) / taskCounts.length
  const stdDev = Math.sqrt(variance)
  
  // Coefficient of variation (CV) - normalize edilmiş standart sapma
  const cv = mean > 0 ? (stdDev / mean) * 100 : 0
  
  // CV'yi 0-100 skoruna çevir (düşük CV = yüksek skor)
  const score = Math.max(0, 100 - cv)
  return Math.round(score)
}

/**
 * Engellenmiş görev oranını hesapla
 */
export function calculateBlockedRatio(blockedTasks: number, totalOpenTasks: number): number {
  if (totalOpenTasks === 0) return 0
  return Math.round((blockedTasks / totalOpenTasks) * 100)
}

/**
 * Genel performans skoru hesapla (ağırlıklı ortalama)
 */
export function calculateOverallScore(metrics: Omit<PerformanceMetrics, 'overallScore'>): number {
  const weights = {
    completionRate: 0.35,      // En önemli: tamamlama
    velocityScore: 0.25,       // Üretkenlik
    workloadScore: 0.20,       // Denge
    blockedRatio: 0.10,        // Risk (ters orantılı)
    avgResponseTime: 0.10,     // Hız (normalize edilmiş)
  }
  
  // Response time'ı skora çevir (düşük = iyi)
  // 24 saat altı = 100, 168 saat (1 hafta) üstü = 0
  const responseScore = Math.max(0, 100 - (metrics.avgResponseTime / 168) * 100)
  
  // Blocked ratio'yu ters çevir (düşük = iyi)
  const blockedScore = 100 - metrics.blockedRatio
  
  // Velocity'yi normalize et (0-100 arası, max 50 görev/hafta = 100)
  const velocityNormalized = Math.min(100, (metrics.velocityScore / 50) * 100)
  
  const score = 
    metrics.completionRate * weights.completionRate +
    velocityNormalized * weights.velocityScore +
    metrics.workloadScore * weights.workloadScore +
    blockedScore * weights.blockedRatio +
    responseScore * weights.avgResponseTime
  
  return Math.round(score)
}

// ============================================================
// KİŞİ PERFORMANS METRİKLERİ
// ============================================================

/**
 * Bir kişinin performans metriklerini hesapla
 */
export function calculatePersonPerformance(person: JiraPersonDetail): PerformanceMetrics {
  const completionRate = calculateCompletionRate(person.openTaskCount, person.completedTaskCount)
  const avgResponseTime = calculateAvgResponseTime([...person.openTasks, ...person.completedTasks])
  const workloadScore = 100 // Tek kişi için her zaman 100
  const velocityScore = calculateVelocity(person.completedTaskCount)
  const blockedRatio = calculateBlockedRatio(person.blockedByIssues.length, person.openTaskCount)
  
  const overallScore = calculateOverallScore({
    completionRate,
    avgResponseTime,
    workloadScore,
    velocityScore,
    blockedRatio,
  })
  
  return {
    completionRate,
    avgResponseTime,
    workloadScore,
    velocityScore,
    blockedRatio,
    overallScore,
  }
}

/**
 * Kişi özetinden basit performans metrikleri
 */
export function calculatePersonSummaryMetrics(person: JiraPersonSummary): Pick<PerformanceMetrics, 'completionRate' | 'velocityScore' | 'overallScore'> {
  const completionRate = calculateCompletionRate(person.openTaskCount, person.completedTaskCount)
  const velocityScore = calculateVelocity(person.completedTaskCount)
  
  // Basitleştirilmiş overall score
  const overallScore = Math.round(
    completionRate * 0.6 + 
    Math.min(100, (velocityScore / 50) * 100) * 0.4
  )
  
  return {
    completionRate,
    velocityScore,
    overallScore,
  }
}

// ============================================================
// TAKIM METRİKLERİ
// ============================================================

/**
 * Takım metriklerini hesapla
 */
export function calculateTeamMetrics(data: JiraPeopleOverviewData): TeamMetrics {
  const taskCounts = data.people.map(p => p.openTaskCount + p.completedTaskCount)
  
  return {
    totalMembers: data.people.length,
    totalOpenTasks: data.totalOpenTasks,
    totalCompletedTasks: data.totalCompletedTasks,
    avgCompletionRate: calculateCompletionRate(data.totalOpenTasks, data.totalCompletedTasks),
    teamVelocity: calculateVelocity(data.totalCompletedTasks),
    workloadBalance: calculateWorkloadBalance(taskCounts),
  }
}

// ============================================================
// TREND VE DAĞILIM VERİLERİ
// ============================================================

/**
 * Günlük tamamlama trendi oluştur (son 30 gün simülasyonu)
 * Gerçek API'den tarihli veri geldiğinde bu fonksiyon güncellenecek
 */
export function generateCompletionTrend(completedTasks: number, days: number = 30): DailyMetric[] {
  const baseDaily = completedTasks / days
  const trend: DailyMetric[] = []
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date()
    date.setDate(date.getDate() - i)
    
    // Hafta sonları daha az, hafta içi daha fazla
    const dayOfWeek = date.getDay()
    const weekendFactor = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.3 : 1
    
    // Rastgele varyasyon ekle (±30%)
    const variation = 0.7 + Math.random() * 0.6
    const count = Math.round(baseDaily * weekendFactor * variation)
    
    trend.push({
      date: date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }),
      value: count,
      count,
    })
  }
  
  return trend
}

/**
 * Kümülatif completion rate trendi
 */
export function generateCumulativeTrend(data: JiraPeopleOverviewData, days: number = 30): DailyMetric[] {
  const totalCompleted = data.totalCompletedTasks
  const totalOpen = data.totalOpenTasks
  const finalRate = calculateCompletionRate(totalOpen, totalCompleted)
  
  const trend: DailyMetric[] = []
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date()
    date.setDate(date.getDate() - i)
    
    // Baştan sona artan trend (simülasyon)
    const progress = (days - i) / days
    const value = Math.round(finalRate * 0.8 + finalRate * 0.2 * progress + (Math.random() - 0.5) * 5)
    
    trend.push({
      date: date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }),
      value: Math.max(0, Math.min(100, value)),
    })
  }
  
  return trend
}

/**
 * Durum dağılımını hesapla
 */
export function calculateStatusDistribution(tasksByStatus: Record<string, number>): StatusDistribution[] {
  const total = Object.values(tasksByStatus).reduce((sum, count) => sum + count, 0)
  if (total === 0) return []
  
  const statusColors: Record<string, string> = {
    'To Do': '#94a3b8',
    'Open': '#94a3b8',
    'Backlog': '#64748b',
    'In Progress': '#3b82f6',
    'In Review': '#8b5cf6',
    'Testing': '#f59e0b',
    'Done': '#22c55e',
    'Closed': '#16a34a',
    'Resolved': '#22c55e',
  }
  
  return Object.entries(tasksByStatus)
    .map(([name, value]) => ({
      name,
      value,
      color: statusColors[name] || '#6b7280',
      percentage: Math.round((value / total) * 100),
    }))
    .sort((a, b) => b.value - a.value)
}

/**
 * Proje dağılımını hesapla
 */
export function calculateProjectDistribution(tasksByProject: Record<string, number>): ProjectDistribution[] {
  const total = Object.values(tasksByProject).reduce((sum, count) => sum + count, 0)
  if (total === 0) return []
  
  return Object.entries(tasksByProject)
    .map(([key, value]) => ({
      key,
      name: key, // API'den name gelirse güncellenebilir
      value,
      percentage: Math.round((value / total) * 100),
    }))
    .sort((a, b) => b.value - a.value)
}

// ============================================================
// LEADERBOARD VE SIRALAMALARI
// ============================================================

export interface LeaderboardEntry {
  rank: number
  user: {
    accountId: string
    displayName: string
    avatarUrl?: string
  }
  completionRate: number
  velocity: number
  openTasks: number
  completedTasks: number
  overallScore: number
  trend: 'up' | 'down' | 'stable'
  badge?: 'gold' | 'silver' | 'bronze'
}

/**
 * Performans sıralaması oluştur
 */
export function createLeaderboard(data: JiraPeopleOverviewData): LeaderboardEntry[] {
  const entries = data.people.map(person => {
    const metrics = calculatePersonSummaryMetrics(person)
    
    return {
      user: {
        accountId: person.user.accountId,
        displayName: person.user.displayName,
        avatarUrl: person.user.avatarUrls?.['48x48'],
      },
      completionRate: metrics.completionRate,
      velocity: metrics.velocityScore,
      openTasks: person.openTaskCount,
      completedTasks: person.completedTaskCount,
      overallScore: metrics.overallScore,
      trend: 'stable' as const, // Gerçek API'den trend verisi geldiğinde güncellenecek
    }
  })
  
  // Sırala ve rank ekle
  entries.sort((a, b) => b.overallScore - a.overallScore)
  
  return entries.map((entry, index) => ({
    ...entry,
    rank: index + 1,
    badge: index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : undefined,
  }))
}

/**
 * En iyi performans gösterenler
 */
export function getTopPerformers(data: JiraPeopleOverviewData, limit: number = 3): LeaderboardEntry[] {
  return createLeaderboard(data).slice(0, limit)
}

/**
 * Dikkat gerektiren kişiler (düşük performans)
 */
export function getNeedsAttention(data: JiraPeopleOverviewData, limit: number = 3): LeaderboardEntry[] {
  return createLeaderboard(data)
    .filter(entry => entry.overallScore < 60 || entry.completionRate < 50)
    .slice(0, limit)
}

// ============================================================
// RADAR CHART VERİSİ
// ============================================================

export interface RadarChartData {
  axis: string
  value: number
  fullMark: number
  category: string
}

/**
 * Kişi için radar chart verisi oluştur
 */
export function createPersonRadarData(person: JiraPersonDetail): RadarChartData[] {
  const metrics = calculatePersonPerformance(person)
  
  return [
    {
      axis: 'Completion',
      value: metrics.completionRate,
      fullMark: 100,
      category: 'completion',
    },
    {
      axis: 'Velocity',
      value: Math.min(100, metrics.velocityScore * 2), // Normalize
      fullMark: 100,
      category: 'velocity',
    },
    {
      axis: 'Balance',
      value: metrics.workloadScore,
      fullMark: 100,
      category: 'balance',
    },
    {
      axis: 'Risk-Free',
      value: 100 - metrics.blockedRatio,
      fullMark: 100,
      category: 'risk',
    },
  ]
}

/**
 * Takım için radar chart verisi oluştur
 */
export function createTeamRadarData(data: JiraPeopleOverviewData): RadarChartData[] {
  const teamMetrics = calculateTeamMetrics(data)
  
  return [
    {
      axis: 'Completion Rate',
      value: teamMetrics.avgCompletionRate,
      fullMark: 100,
      category: 'completion',
    },
    {
      axis: 'Team Velocity',
      value: Math.min(100, (teamMetrics.teamVelocity / 100) * 100), // Normalize
      fullMark: 100,
      category: 'velocity',
    },
    {
      axis: 'Workload Balance',
      value: teamMetrics.workloadBalance,
      fullMark: 100,
      category: 'balance',
    },
    {
      axis: 'Team Size',
      value: Math.min(100, teamMetrics.totalMembers * 10), // Normalize
      fullMark: 100,
      category: 'size',
    },
  ]
}

// ============================================================
// PERFORMANS SEVİYESİ ETİKETLERİ
// ============================================================

export type PerformanceLevel = 'excellent' | 'good' | 'average' | 'needs_improvement' | 'critical'

export function getPerformanceLevel(score: number): PerformanceLevel {
  if (score >= 90) return 'excellent'
  if (score >= 75) return 'good'
  if (score >= 60) return 'average'
  if (score >= 40) return 'needs_improvement'
  return 'critical'
}

export function getPerformanceLevelConfig(level: PerformanceLevel) {
  const configs = {
    excellent: {
      label: 'Mükemmel',
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
      icon: '🌟',
    },
    good: {
      label: 'İyi',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      icon: '✅',
    },
    average: {
      label: 'Ortalama',
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
      icon: '📊',
    },
    needs_improvement: {
      label: 'Geliştirilmeli',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
      icon: '⚠️',
    },
    critical: {
      label: 'Kritik',
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      icon: '🚨',
    },
  }
  
  return configs[level]
}

